import React, { Component } from 'react';
import { drawCircle, drawLine, clearCtx, fadeCtx } from './drawhelp';

import * as fourier from './fourier';
import { FillInGaps, Normalize } from './HelpFunctions';

class MiniDraw extends Component {
	constructor(props) {
		super(props);
		this.canvas = React.createRef();

		this.state = {
			running: false,
			points: []
		}
		this.current_time = 0;
		this.sleep = 20;

		this.sample_step = 0.005;
		this.circles = [];
		this.max_circles = 800;
		this.zoomfactor = 1;

		this.step_size = 0.01;

		this.add_circle_interval = 50;
		this.counter = 0;

		this.zoom = {
			x: 0,
			y: 0,
			ratio: 1
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
		this.stop();
	}

	componentDidMount() {
		this._isMounted = true;

		this.ctx = this.canvas.current.getContext("2d");
		this.origin_canvas = document.createElement('canvas');
		this.trace_canvas = document.createElement('canvas');
		this.circle_canvas = document.createElement('canvas');

		let width = this.canvas.current.width;
		let height = this.canvas.current.height;
		this.trace_canvas.width = width;
		this.trace_canvas.height = height;

		this.circle_canvas.width = width;
		this.circle_canvas.height = height;

		this.origin_canvas.width = width;
		this.origin_canvas.height = height;

		this.origin_ctx = this.origin_canvas.getContext("2d");
		this.trace_ctx = this.trace_canvas.getContext("2d");
		this.circle_ctx = this.circle_canvas.getContext("2d");

		this.newPoints();
		this.drawOrigin();

		this.start();
	}

	newPoints() {
		let points = [];
		this.trace_path = [];
		if (this.props.points) {
			points = FillInGaps(this.props.points);
			if (this.props.normalize_margin > 0)
				points = Normalize(points, this.props.normalize_margin);
		}
		this.setState({ points: points });
	}

	getRadiusForZoom(zoom_to_circle) {
		let arr = [];
		for (let pi = 0; pi < Math.PI * 2; pi += .1) {
			let x = 0;
			let y = 0;
			for (let i = zoom_to_circle; i < this.circles.length; i++) {
				[x, y] = fourier.getPointOnCircle(this.circles[i], pi, x, y);
			}
			arr.push(Math.sqrt(x * x + y * y));
		}
		let radius = Math.max(...arr);
		return radius;
	}

	setZoom(zoom_to_circle) {
		if (zoom_to_circle > this.circles.length)
			zoom_to_circle = this.circles.length;

		let circle = this.circles[zoom_to_circle - 1];
		if (circle) {
			this.zoom = {
				...this.zoom,
				x: circle.center_x,
				y: circle.center_y
			}
		}
	}

	copyToScreen(ctx) {
		this.ctx.drawImage(ctx.canvas, 0, 0);
	}

	drawOrigin() {
		let x1 = undefined, y1;

		for (let time = 0; time < Math.PI * 2; time += this.sample_step) {
			let [x2, y2, ignore] = fourier.getOrigin(this.state.points, time);
			if (ignore) {
				x2 = undefined;
			} else if (x1 !== undefined) {
				drawLine(
					this.origin_ctx, x1, y1, x2, y2,
					{
						color: this.props.settings.origin_color,
						width: this.props.settings.origin_pixels
					},
					this.zoom);
			}
			[x1, y1] = [x2, y2];
		}

		// Draw a cross at origo
		drawLine(
			this.origin_ctx, 0, .9, 0, -.9,
			{ color: "#ddd" }, this.zoom);
		drawLine(
			this.origin_ctx, .9, 0, -.9, 0,
			{ color: "#ddd" }, this.zoom);
	}

	start() {
		setTimeout(() => this.interval(), 100);
		this.setState({
			running: true
		});
	}

	stop() {
		this.setState({
			running: false
		})
	}

	// Draw and step forward
	interval() {
		if (this._isMounted && this.props.running) {
			this.counter++;
			if (this.circles.length === 0 || this.counter === this.add_circle_interval) {
				this.counter = Math.min(this.circles.length, this.add_circle_interval - 10);
				if (this.props.settings.number_of_circles > this.circles.length) {
					let circle = fourier.findBestCircle(this.circles, this.state.points, this.sample_step);
					this.circles.push(circle);
				}
			}
			{
				// Next step
				let time = this.current_time;
				time += this.step_size * (this.props.settings.speed / 20) * this.zoom.ratio;
				if (time > Math.PI * 2) {
					time -= Math.PI * 2;
				}
				if (Math.floor(time) !== Math.floor(time + this.step_size)) {
					fadeCtx(this.trace_ctx, .2, this.ctx);
				}
				this.drawLayout(time, this.circles.length);

				this.current_time = time;
			}
			setTimeout(() => this.interval(), this.sleep);
		}
	}

	drawPath(time, number_of_circles) {
		// remove warning on line below
		// eslint-disable-next-line
		let [dummy1, dummy2, start] = fourier.getOrigin(this.state.points, time);

		// Draw trace
		let pos = {
			x: this.circles[number_of_circles - 1].x,
			y: this.circles[number_of_circles - 1].y,
			start: start,
			time: time
		};

		this.trace_path.unshift(pos);
		{
			let step = this.step_size * this.props.settings.speed / 20;
			let trace_path_max = Math.ceil((Math.PI * 2) / step);
			this.trace_path = this.trace_path.slice(0, trace_path_max);
		}

		let last_pos;
		clearCtx(this.trace_ctx);
		for (let i = 0; i < this.trace_path.length; i++) {
			let pos = this.trace_path[i];
			let alpha = Math.ceil(255 - 255 * (i / (2 * this.trace_path.length))).toString(16).padStart(2, "0");
			if (last_pos) {
				drawLine(this.trace_ctx,
					pos.x, pos.y,
					last_pos.x, last_pos.y,
					{
						color: this.props.settings.path_color + alpha,
						width: pos.start ? 1 : this.props.settings.path_pixels
					},
					this.zoom);
			}
			last_pos = pos;
		}
	}

	drawLayout(time, number_of_circles) {
		fourier.updateCirclePositions(this.circles, time);

		// Update where and how we zoom
		this.setZoom(this.props.settings.zoom_to_circle)

		clearCtx(this.ctx);

		if (this.props.settings.origin_show) {
			if (this.props.settings.zoom_to_circle !== 0) {
				clearCtx(this.origin_ctx);
				this.drawOrigin();
			}
			this.copyToScreen(this.origin_ctx);
		}

		if (number_of_circles > 0) {
			this.drawPath(time, number_of_circles);
			if (this.props.settings.path_show) {
				this.copyToScreen(this.trace_ctx);
			}

			clearCtx(this.circle_ctx);
			if (this.props.settings.circles_show) {

				for (let i = 0; i < number_of_circles; i++) {
					if (this.props.settings.zoom_to_circle - 1 <= i) {
						let circle = this.circles[i];
						drawCircle(
							this.circle_ctx,
							circle.center_x,
							circle.center_y,
							circle.radius,
							{
								color: this.props.settings.circles_color,
								width: this.props.settings.circles_pixels
							},
							this.zoom);
					}
				}
			}

			if (this.props.settings.arrows_show) {
				for (let i = 0; i < number_of_circles; i++) {
					if (this.props.settings.zoom_to_circle - 1 <= i) {
						let [x1, y1] = [this.circles[i].center_x, this.circles[i].center_y];
						let [x2, y2] = [this.circles[i].x, this.circles[i].y];
						drawLine(
							this.circle_ctx, x1, y1, x2, y2,
							{
								color: this.props.settings.arrows_color,
								width: this.props.settings.arrows_pixels
							},
							this.zoom);
					}
				}
			}
			this.copyToScreen(this.circle_ctx);

			if (this.props.settings.text_show) {
				this.ctx.font = "16px Arial";
				this.ctx.fillText(this.circles.length, 10, this.props.height - 2);
			}
		}
	}

	settingchange(name, value) {
		if (name === "zoom_to_circle") {
			let radius = this.getRadiusForZoom(value);
			this.zoom = { ratio: value === 1 ? 1 : radius };
			this.setZoom(value);
		}
	}


	render() {
		if (!this.props)
			return <></>;

		// eslint-disable-next-line
		if (this.props.points != this.state.points_copy) {
			this.setState({ points_copy: this.props.points });
			this.circles = [];

			this.newPoints();
		}

		return (
			<div
				style={this.props.style}
				onClick={this.props.onClick}
			>
				<canvas
					ref={this.canvas}
					width={this.props.width}
					height={this.props.height}
					style={{ border: "1px solid black" }}
				/>
			</div>
		)
	}
}

export default MiniDraw;